import { Log, Lightning } from '@lightningjs/sdk'

export default class VideoTexture extends Lightning.Component {
    static override _template() {
        return {
            Video: {
                alpha: 1,
                visible: false,
                pivot: 0.5,
                texture: { type: Lightning.textures.StaticTexture, options: {} },
            },
        }
    }

    private _videoEl: HTMLVideoElement | any;
    private _lastWidth: number = 0;
    private _lastHeight: number = 0;
    private _lastTime: number = 0;
    private _usingRvfc: boolean = false;
    private _rvfcId: any;
    private _updateVideoTexture: any;
    private _updatingVideoTexture: boolean = false;
    private _lastFrame: any;

    set videoEl(v) {
        this._videoEl = v
        this._createVideoTexture()
    }

    get videoEl() {
        return this._videoEl
    }

    get videoView() {
        return this.tag('Video')
    }

    get videoTexture() {
        return this.videoView.texture
    }

    get isVisible() {
        return this.videoView.alpha === 1 && this.videoView.visible === true
    }

    override _init() {
        if (this._videoEl) {
            this._createVideoTexture()
        }
    }

    _createVideoElement() {
        const videoEl = document.createElement('video')
        videoEl.style.position = 'absolute'
        videoEl.style.display = 'block'
        videoEl.style.visibility = 'visible'
        videoEl.style.opacity = '0'
        videoEl.style.pointerEvents = 'none'
        videoEl.style.zIndex = '-1'
        videoEl.setAttribute('crossorigin', 'anonymous')
        document.body.appendChild(videoEl)
        return videoEl
    }

    _createVideoTexture() {
        if (!this.videoEl) {
            // console.warn('VideoTexture: videoEl not set, skipping texture creation')
            return
        }

        const stage = this.stage

        const gl = stage.gl
        if (!gl) {
            console.warn('VideoTexture: GL context not available')
            return
        }
        const glTexture = gl.createTexture()
        gl.bindTexture(gl.TEXTURE_2D, glTexture)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
        this.videoTexture.options = {
            source: glTexture,
            w: this.videoEl.width || 1920,
            h: this.videoEl.height || 1080,
        }

        this.videoView.w = (this.videoEl.width || 1920)
        this.videoView.h = (this.videoEl.height || 1080)
    }

    _render() {
        const stage = this.stage
        const gl = stage.gl
        try {
            if (!this.videoEl) return

            gl?.bindTexture(gl.TEXTURE_2D, this.videoTexture.options.source)
            gl?.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
            gl?.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.videoEl)
            this.videoView.visible = true

            // Only recalculate aspect ratio if dimensions changed
            if (this._lastWidth !== this.videoEl.width || this._lastHeight !== this.videoEl.height) {
                this._lastWidth = this.videoEl.width
                this._lastHeight = this.videoEl.height

                this.videoTexture.options.w = this.videoEl.width
                this.videoTexture.options.h = this.videoEl.height
                // Logic to preserve aspect ratio (Letterboxing) - Keep this as per requirements
                const expectedAspectRatio = this.videoView.w / this.videoView.h
                const realAspectRatio = this.videoEl.width / this.videoEl.height

                if (expectedAspectRatio > realAspectRatio) {
                    this.videoView.scaleX = realAspectRatio / expectedAspectRatio
                    this.videoView.scaleY = 1
                } else {
                    this.videoView.scaleY = expectedAspectRatio / realAspectRatio
                    this.videoView.scaleX = 1
                }
            }
            this.videoTexture.source.forceRenderUpdate()
        } catch (e) {
            Log.error('texImage2d video', e)
            this.stop()
        }
    }

    start() {
        const stage = this.stage
        this._lastTime = 0
        this._lastWidth = 0
        this._lastHeight = 0

        if (this.videoEl && typeof this.videoEl.requestVideoFrameCallback === 'function') {
            this._usingRvfc = true
            const rvfcLoop = () => {
                if (!this._usingRvfc || !this.active) return
                if (this.videoTexture.options.source && this.videoEl && this.videoEl.videoWidth) {
                    this._render()
                }
                if (this._usingRvfc && this.videoEl) {
                    this._rvfcId = this.videoEl.requestVideoFrameCallback(rvfcLoop)
                }
            }
            this._rvfcId = this.videoEl.requestVideoFrameCallback(rvfcLoop)
        } else {
            const targetFps = 30
            const frameInterval = 1000 / targetFps

            if (!this._updateVideoTexture) {
                this._updateVideoTexture = () => {
                    if (this.videoTexture.options.source && this.videoEl && this.videoEl.videoWidth && this.active) {
                        const currentTime = Date.now()
                        const timeSinceLastUpdate = currentTime - this._lastTime

                        if (timeSinceLastUpdate < frameInterval) {
                            return
                        }

                        const getVideoPlaybackQuality = this.videoEl.getVideoPlaybackQuality && this.videoEl.getVideoPlaybackQuality()
                        const frameCount = getVideoPlaybackQuality
                            ? getVideoPlaybackQuality.totalVideoFrames
                            : (this.videoEl.webkitDecodedFrameCount || 0)

                        const mustUpdate = frameCount
                            ? this._lastFrame !== frameCount
                            : true

                        if (mustUpdate) {
                            this._lastTime = currentTime
                            this._lastFrame = frameCount
                            this._render()
                        }
                    }
                }
            }
            if (!this._updatingVideoTexture) {
                stage.on('frameStart', this._updateVideoTexture)
                this._updatingVideoTexture = true
            }
        }
    }

    stop() {
        const stage = this.stage
        if (this._updatingVideoTexture) {
            stage.removeListener('frameStart', this._updateVideoTexture)
            this._updatingVideoTexture = false
        }

        if (this._usingRvfc && this._rvfcId !== undefined && this.videoEl) {
            this.videoEl.cancelVideoFrameCallback(this._rvfcId)
            this._usingRvfc = false
        }

        this.videoView.visible = false

        if (this.videoTexture.options.source) {
            const gl = stage.gl
            gl?.bindTexture(gl.TEXTURE_2D, this.videoTexture.options.source)
            gl?.clearColor(0, 0, 0, 1)
            gl?.clear(gl.COLOR_BUFFER_BIT)
        }

        if (this.videoEl) {
            this.videoEl.pause();
            this.videoEl.removeAttribute('src');
            this.videoEl.load();
        }
    }

    position(top: any, left: any) {
        this.videoView.patch({
            x: left,
            y: top,
        })
    }

    size(width: any, height: any) {
        this.videoView.patch({
            w: width,
            h: height,
        })
    }

    show() {
        this.videoView.alpha = 1
    }

    hide() {
        this.videoView.alpha = 0
    }

    // Player API methods
    open(url: string) {
        if (this.videoEl) {
            this.videoEl.crossOrigin = 'anonymous'
            this.videoEl.src = url
            this.videoEl.load()
            this.start()
            this.show()
        }
    }

    play() {
        if (this.videoEl) {
            this.videoEl.play().catch((e: any) => console.error(e))
            this.start()
        }
    }

    pause() {
        if (this.videoEl) this.videoEl.pause()
    }

    mute(val: boolean) {
        if (this.videoEl) this.videoEl.muted = val
    }

    loop(val: boolean) {
        if (this.videoEl) this.videoEl.loop = val
    }

    resize(w: number, h: number) {
        this.size(w, h);
    }

    videoElementSize(w: number, h: number) {
        if (this.videoEl) {
            this.videoEl.width = w;
            this.videoEl.height = h;
        }
    }

    override _detach() {
        this.stop();
        // Do not remove the video element if it is the global content-player
        if (this.videoEl && this.videoEl.parentNode && this.videoEl.id !== 'content-player') {
            this.videoEl.parentNode.removeChild(this.videoEl);
        }
        super._detach();
    }
}